import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { usePaymentMethodsApi } from "@/Hooks/usePaymentMethodsApi";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import type { BankAccount } from "@freightsimple/generated-dashboard-openapi-client/models/BankAccount";
import type { CreditCard } from "@freightsimple/generated-dashboard-openapi-client/models/CreditCard";
import { BankAccountVerificationStatus } from "@freightsimple/generated-dashboard-openapi-client/models/BankAccountVerificationStatus";
import { PaymentMethod } from "@freightsimple/generated-dashboard-openapi-client/models/PaymentMethod";
import { useAuthentication } from "@/Slices/authenticationSlice";
import { useBankAccountsAddNew } from "./useBankAccountsAddNew";
import { BankAccountsVerifyMicrodepositsDialog } from "../Dialogs/BankAccountsVerifyMicrodepositsDialog";

/**
 * Owns the Bank Accounts screen's data and actions — load, add (making the new
 * account the company default), remove (with a confirm), verify microdeposits,
 * and set-default — so the screen itself is render-only. A failed remove or
 * set-default opens the error notice. Also owns the screen's dialogs
 * (declarative): render the returned `dialogs` node.
 */
export function useBankAccounts() {
  const [bankAccounts, setBankAccounts] = useState<Array<BankAccount>>([]);
  const [creditCards, setCreditCards] = useState<Array<CreditCard>>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [allowRemoveAccountButton, setAllowRemoveAccountButton] =
    useState(false);
  const { user } = useAuthentication();

  const [removeTarget, setRemoveTarget] = useState<BankAccount | undefined>();
  const [verifyTarget, setVerifyTarget] = useState<BankAccount | undefined>();
  const [errorNotice, setErrorNotice] = useState<string | undefined>();

  const paymentMethodsApi = usePaymentMethodsApi();

  async function loadBankAccounts() {
    setLoadFailed(false);
    try {
      const response = await paymentMethodsApi.getAllPaymentMethods();
      setBankAccounts(response.bankAccounts);
      setCreditCards(response.creditCards);
      // Pending bank accounts can't be charged, so they don't count toward
      // "the customer still has at least one usable payment method" — guard
      // against letting someone remove their only verified method.
      const verifiedBankAccounts = response.bankAccounts.filter(
        (b) => b.verificationStatus === BankAccountVerificationStatus.Verified,
      );
      setAllowRemoveAccountButton(
        response.creditCards.length + verifiedBankAccounts.length > 1,
      );
    } catch (e) {
      console.warn("Error loading payment methods:", { e });
      setLoadFailed(true);
    } finally {
      setInitialLoading(false);
    }
  }

  function retryLoad() {
    setInitialLoading(true);
    loadBankAccounts();
  }

  const addBankAccountFlow = useBankAccountsAddNew(async function (
    newPaymentMethodId: string | undefined,
  ) {
    // If we did successully add a new bank account, make that
    // the default payment method for this company
    if (newPaymentMethodId) {
      await paymentMethodsApi.setDefaultPaymentMethod({
        setDefaultPaymentMethodRequest: {
          stripePaymentMethodId: newPaymentMethodId,
          paymentMethod: PaymentMethod.BankAccount,
          newPaymentMethod: true,
        },
      });
    }
    // update the booking stages for the shipments
    if (user?.companyId) {
      await paymentMethodsApi.onPaymentMethodAdded({
        onPaymentMethodAddedRequest: {
          companyId: user.companyId,
        },
      });
    }
    await loadBankAccounts();
  });

  useEffect(() => {
    loadBankAccounts();
  }, []);

  function removeBankAccount(stripePaymentMethodId: string) {
    const bankAccount = bankAccounts.find(
      (b) => b.stripePaymentMethodId === stripePaymentMethodId,
    );
    if (!bankAccount) return;
    setRemoveTarget(bankAccount);
  }

  async function onRemoveConfirmed() {
    if (removeTarget === undefined) return;
    setRemoveTarget(undefined);
    try {
      await paymentMethodsApi.removePaymentMethod({
        removePaymentMethodRequest: {
          stripePaymentMethodId: removeTarget.stripePaymentMethodId,
          // For a pending account the PaymentMethod isn't attached yet — the
          // backend cancels this SetupIntent instead of detaching. Undefined
          // for verified accounts, which fall back to the detach path.
          ...(removeTarget.setupIntentId !== undefined
            ? { setupIntentId: removeTarget.setupIntentId }
            : {}),
        },
      });
    } catch (e) {
      console.warn("Error removing bank account:", { e });
      setErrorNotice(
        "We couldn't remove this bank account. Something went wrong, please try again.",
      );
    }
    await loadBankAccounts();
  }

  // Opens the microdeposit verification dialog for a pending account; reloads
  // the list on success so the now-verified account drops its pending state.
  function verifyBankAccount(bankAccount: BankAccount) {
    if (!bankAccount.setupIntentId) {
      // Defensive: pending bank accounts always come with a setupIntentId from
      // the backend. Bail loudly instead of POSTing an empty string.
      setErrorNotice(
        "We couldn't locate this pending bank account. Refresh the page and try again.",
      );
      return;
    }
    setVerifyTarget(bankAccount);
  }

  async function setDefaultBankAccount(stripePaymentMethodId: string) {
    try {
      await paymentMethodsApi.setDefaultPaymentMethod({
        setDefaultPaymentMethodRequest: {
          stripePaymentMethodId: stripePaymentMethodId,
          paymentMethod: PaymentMethod.BankAccount,
          newPaymentMethod: false,
        },
      });
    } catch (e) {
      console.warn("Error setting default bank account:", { e });
      setErrorNotice(
        "We couldn't make this your default payment method. Something went wrong, please try again.",
      );
    }
    await loadBankAccounts();
  }

  const noBankAccounts =
    bankAccounts === undefined || bankAccounts.length === 0;

  const dialogs: ReactNode = (
    <>
      {addBankAccountFlow.dialogs}
      <Dialog
        open={removeTarget !== undefined}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(undefined);
        }}
        title="Confirm"
        description="Are you sure you want to remove this bank account"
        okText="Remove Bank Account"
        onOk={onRemoveConfirmed}
      >
        <ControlPresentationProvider presentation="underlined">
          <></>
        </ControlPresentationProvider>
      </Dialog>
      <Dialog
        title="Verify your bank account"
        open={verifyTarget !== undefined}
        onOpenChange={(open) => {
          if (!open) setVerifyTarget(undefined);
        }}
        width="narrow"
      >
        <ControlPresentationProvider presentation="underlined">
          {verifyTarget && verifyTarget.setupIntentId && (
            <BankAccountsVerifyMicrodepositsDialog
              bankName={verifyTarget.bankName}
              lastFourDigits={verifyTarget.lastFourDigits}
              onCancel={() => setVerifyTarget(undefined)}
              onConfirm={async (firstAmountCents, secondAmountCents) => {
                const setupIntentId = verifyTarget.setupIntentId;
                if (setupIntentId === undefined) {
                  return;
                }
                const response = await paymentMethodsApi.verifyMicrodeposits({
                  verifyMicrodepositsRequest: {
                    setupIntentId,
                    firstAmountCents,
                    secondAmountCents,
                  },
                });
                if (!response.success) {
                  // Surface the friendly Stripe error inline in the modal so the
                  // customer can correct the amounts without losing context.
                  throw new Error(
                    response.errorMessage ??
                      "We couldn't verify the micro-deposits. Please double-check the amounts and try again.",
                  );
                }
                setVerifyTarget(undefined);
                await loadBankAccounts();
              }}
            />
          )}
        </ControlPresentationProvider>
      </Dialog>
      <Dialog
        open={errorNotice !== undefined}
        onOpenChange={(open) => {
          if (!open) setErrorNotice(undefined);
        }}
        title="Error"
        footer={
          <Button primary onClick={() => setErrorNotice(undefined)}>
            OK
          </Button>
        }
      >
        <ControlPresentationProvider presentation="underlined">
          {errorNotice}
        </ControlPresentationProvider>
      </Dialog>
    </>
  );

  return {
    bankAccounts,
    creditCards,
    initialLoading,
    /** Set when the accounts failed to load: Try again on the failed-load state. */
    retryFailedLoad: loadFailed ? retryLoad : undefined,
    allowRemoveAccountButton,
    noBankAccounts,
    addNewBankAccount: addBankAccountFlow.start,
    removeBankAccount,
    verifyBankAccount,
    setDefaultBankAccount,
    dialogs,
  };
}
